/**
 * Друкована форма «Акт приймання продукції з переробки» (довільна форма,
 * ч.686): організація-замовник, № і дата, переробник + договір, склад;
 * секції: Продукція (к-сть, планова; собівартість для проведеного — під
 * галкою «показати вартість», ч.678), Послуги переробника (з ПДВ),
 * Використані матеріали, Повернені матеріали (склад по рядках, 0464);
 * підписи. Єдиний стиль друку (канон ч.669/677).
 */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { processingReceipts } from '@/api/processingReceipts';
import { orgs, contractors as contractorsApi, nomenclature as nomApi } from '@/api/catalogs';
import { refs } from '@/api/refs';
import PrintActions from '@/shared/components/PrintActions';
import { contractLabel } from '@/shared/utils/labels';
import { partyCodesText } from '@/shared/utils/partyCodes';

const fmtQty = (n: number | string) =>
  Number(n).toLocaleString('uk-UA', { maximumFractionDigits: 3 });
const fmtMoney = (n: number | string | null | undefined) =>
  Number(n ?? 0).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function ProcessingReceiptPrintPage() {
  const { id } = useParams<{ id: string }>();
  // ч.678-канон: собівартість (наша) за замовчуванням не друкується;
  // суми ПОСЛУГ переробника — його ж рахунок, друкуються завжди.
  const [showCost, setShowCost] = useState(false);

  useEffect(() => {
    document.body.classList.add('print-mode');
    return () => document.body.classList.remove('print-mode');
  }, []);

  const docQ = useQuery({
    queryKey: ['processing-receipt', id],
    queryFn: () => processingReceipts.get(id!),
    enabled: !!id,
  });
  const d = docQ.data;
  const orgQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const org = orgQ.data?.items.find((o) => o.id === d?.organization_id);
  const cQ = useQuery({ queryKey: ['contractors-all'], queryFn: () => contractorsApi.listAll() });
  const contractsQ = useQuery({
    queryKey: ['contracts', d?.organization_id, d?.processor_contractor_id],
    queryFn: () => refs.contracts({
      organization_id: d!.organization_id,
      contractor_id: d!.processor_contractor_id,
    }),
    enabled: !!d?.organization_id && !!d?.processor_contractor_id,
  });
  const whQ = useQuery({
    queryKey: ['warehouses', d?.organization_id],
    queryFn: () => refs.warehouses(d!.organization_id),
    enabled: !!d?.organization_id,
  });
  const nomListQ = useQuery({
    queryKey: ['nomenclature-full'],
    queryFn: () => nomApi.listAll(),
  });
  const uomsQ = useQuery({ queryKey: ['uoms'], queryFn: refs.uoms });

  if (!d || !org) {
    return <div style={{ padding: '2rem' }}>Завантаження акта…</div>;
  }

  const whName = new Map((whQ.data ?? []).map((w) => [w.id, w.name]));
  const nomById = new Map((nomListQ.data?.items ?? []).map((n) => [n.id, n]));
  const uomById = new Map((uomsQ.data ?? []).map((u) => [u.id, u.short_name]));
  const processor = cQ.data?.items.find((c) => c.id === d.processor_contractor_id);
  const contract = (contractsQ.data ?? []).find((c) => c.id === d.contract_id);
  const uomOf = (it: { nomenclature_id: string; uom_id?: string | null }) => {
    if (it.uom_id) return uomById.get(it.uom_id) ?? 'шт';
    const n = nomById.get(it.nomenclature_id);
    return (n?.base_uom_id && uomById.get(n.base_uom_id)) || 'шт';
  };
  const nomCell = (nid: string) => {
    const n = nomById.get(nid);
    return n ? <>{n.name}{n.code && <span style={{ color: '#555' }}> ({n.code})</span>}</> : <>—</>;
  };
  const services = d.services ?? [];
  const returned = d.returned_materials ?? [];
  const svcTotal = services.reduce((s, x) => s + Number(x.amount ?? 0), 0);
  const svcVat = services.reduce((s, x) => s + Number(x.vat_amount ?? 0), 0);
  const svcShown = services.length > 0;
  const hdrSvc = !svcShown && Number(d.processing_service_amount) > 0;
  const prodTotal = d.products.reduce((s, p) => s + Number(p.total_cost ?? 0), 0);
  // ч.685: повернення можуть їхати на ІНШИЙ склад — колонка при >1 ефективному.
  const retWh = (it: { warehouse_id?: string | null }) => it.warehouse_id || d.warehouse_id;
  const retMulti = new Set(returned.map(retWh)).size > 1;

  return (
    <div className="print-page" style={{
      maxWidth: 800, margin: '0 auto', padding: '1.5rem',
      fontFamily: "'Times New Roman', serif", fontSize: '10.5pt', background: 'var(--bg-panel)',
    }}>
      <style>{`
        .pr2-table { width: 100%; border-collapse: collapse; margin: 0.35rem 0 0.75rem; font-size: 9pt; line-height: 1.15; }
        .pr2-table th, .pr2-table td { border: 1px solid #000; padding: 1px 4px; }
        .pr2-table th { text-align: center; font-weight: 600; font-size: 8pt; background: #f5f5f5; text-transform: none; letter-spacing: 0; }
        .pr2-sec { font-weight: 700; margin: 0.6rem 0 0; }
      `}</style>

      <PrintActions docMeta={{
        organizationId: d.organization_id,
        documentType: 'doc.processing_receipts',
        documentId: d.id,
        filename: `Надходження з переробки ${d.document_number}`,
        orgName: org.name,
      }} />

      {d.posted && (
        <label className="print-hide" style={{
          display: 'flex', alignItems: 'center', gap: '0.4rem',
          fontSize: '0.85rem', marginBottom: '0.75rem', cursor: 'pointer',
        }}>
          <input type="checkbox" checked={showCost}
            onChange={(e) => setShowCost(e.target.checked)}
            style={{ width: 'auto' }} />
          показати собівартість (для внутрішнього примірника)
        </label>
      )}

      <div style={{ textAlign: 'center', marginBottom: '0.5rem' }}>
        <div style={{ fontSize: '10pt' }}>{org.full_name || org.name}</div>
        {partyCodesText(org) && <div style={{ fontSize: '10pt', color: '#555' }}>{partyCodesText(org)}</div>}
      </div>

      <h2 style={{ textAlign: 'center', margin: '0.75rem 0' }}>
        Акт приймання продукції з переробки № {d.document_number}
        {' '}від {new Date(d.document_date).toLocaleDateString('uk-UA')}
      </h2>

      <table style={{ width: '100%', fontSize: '10pt', marginBottom: '0.25rem' }}>
        <tbody>
          <tr>
            <td style={{ width: '55%' }}>
              <span style={{ color: '#555' }}>Переробник: </span>
              <strong>{processor?.name ?? '—'}</strong>
              {partyCodesText(processor) && <span style={{ color: '#555' }}> ({partyCodesText(processor)})</span>}
            </td>
            <td>
              <span style={{ color: '#555' }}>Договір: </span>
              {contract ? contractLabel(contract) : 'без договору'}
            </td>
          </tr>
          <tr>
            <td colSpan={2}>
              <span style={{ color: '#555' }}>Склад оприбуткування: </span>
              {whName.get(d.warehouse_id) ?? '—'}
            </td>
          </tr>
        </tbody>
      </table>

      <div className="pr2-sec">Отримана продукція</div>
      <table className="pr2-table">
        <thead>
          <tr>
            <th style={{ width: 30 }}>№</th>
            <th>Продукція</th>
            <th style={{ width: 45 }}>Од.</th>
            <th style={{ width: 80 }}>К-сть</th>
            {d.posted && showCost && <th style={{ width: 95 }}>Собівартість</th>}
          </tr>
        </thead>
        <tbody>
          {d.products.map((p, i) => (
            <tr key={i}>
              <td style={{ textAlign: 'center' }}>{i + 1}</td>
              <td>{nomCell(p.nomenclature_id)}</td>
              <td style={{ textAlign: 'center' }}>{uomOf(p)}</td>
              <td style={{ textAlign: 'right' }}>{fmtQty(p.quantity)}</td>
              {d.posted && showCost && (
                <td style={{ textAlign: 'right' }}>{fmtMoney(p.total_cost)}</td>
              )}
            </tr>
          ))}
        </tbody>
        {d.posted && showCost && (
          <tfoot>
            <tr style={{ fontWeight: 700 }}>
              <td colSpan={4} style={{ textAlign: 'right' }}>Разом:</td>
              <td style={{ textAlign: 'right' }}>{fmtMoney(prodTotal)}</td>
            </tr>
          </tfoot>
        )}
      </table>

      {(svcShown || hdrSvc) && (<>
        <div className="pr2-sec">Послуги переробника</div>
        <table className="pr2-table">
          <thead>
            <tr>
              <th style={{ width: 30 }}>№</th>
              <th>Найменування послуги</th>
              <th style={{ width: 70 }}>К-сть</th>
              <th style={{ width: 80 }}>Ціна</th>
              <th style={{ width: 90 }}>Сума</th>
              <th style={{ width: 85 }}>ПДВ</th>
            </tr>
          </thead>
          <tbody>
            {svcShown ? services.map((sv, i) => (
              <tr key={i}>
                <td style={{ textAlign: 'center' }}>{i + 1}</td>
                <td>
                  {sv.description
                    || (sv.nomenclature_id ? nomById.get(sv.nomenclature_id)?.name : '') || '—'}
                </td>
                <td style={{ textAlign: 'right' }}>{fmtQty(sv.quantity ?? '1')}</td>
                <td style={{ textAlign: 'right' }}>{sv.price != null ? fmtMoney(sv.price) : '—'}</td>
                <td style={{ textAlign: 'right' }}>{fmtMoney(sv.amount)}</td>
                <td style={{ textAlign: 'right' }}>{fmtMoney(sv.vat_amount)}</td>
              </tr>
            )) : (
              <tr>
                <td style={{ textAlign: 'center' }}>1</td>
                <td>Послуги з переробки давальницької сировини</td>
                <td style={{ textAlign: 'right' }}>1</td>
                <td style={{ textAlign: 'right' }}>—</td>
                <td style={{ textAlign: 'right' }}>{fmtMoney(d.processing_service_amount)}</td>
                <td style={{ textAlign: 'right' }}>{fmtMoney(d.processing_service_vat)}</td>
              </tr>
            )}
          </tbody>
          <tfoot>
            <tr style={{ fontWeight: 700 }}>
              <td colSpan={4} style={{ textAlign: 'right' }}>Разом послуг:</td>
              <td style={{ textAlign: 'right' }}>
                {fmtMoney(svcShown ? svcTotal : d.processing_service_amount)}
              </td>
              <td style={{ textAlign: 'right' }}>
                {fmtMoney(svcShown ? svcVat : d.processing_service_vat)}
              </td>
            </tr>
          </tfoot>
        </table>
      </>)}

      {d.materials.length > 0 && (<>
        <div className="pr2-sec">Використана сировина замовника</div>
        <table className="pr2-table">
          <thead>
            <tr>
              <th style={{ width: 30 }}>№</th>
              <th>Сировина</th>
              <th style={{ width: 45 }}>Од.</th>
              <th style={{ width: 80 }}>К-сть</th>
              {d.posted && showCost && <th style={{ width: 95 }}>Собівартість</th>}
            </tr>
          </thead>
          <tbody>
            {d.materials.map((m, i) => (
              <tr key={i}>
                <td style={{ textAlign: 'center' }}>{i + 1}</td>
                <td>{nomCell(m.nomenclature_id)}</td>
                <td style={{ textAlign: 'center' }}>{uomOf(m)}</td>
                <td style={{ textAlign: 'right' }}>{fmtQty(m.quantity)}</td>
                {d.posted && showCost && (
                  <td style={{ textAlign: 'right' }}>{fmtMoney(m.total_cost)}</td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </>)}

      {returned.length > 0 && (<>
        <div className="pr2-sec">Повернена невикористана сировина</div>
        <table className="pr2-table">
          <thead>
            <tr>
              <th style={{ width: 30 }}>№</th>
              <th>Сировина</th>
              {retMulti && <th style={{ width: 130 }}>Склад повернення</th>}
              <th style={{ width: 45 }}>Од.</th>
              <th style={{ width: 80 }}>К-сть</th>
            </tr>
          </thead>
          <tbody>
            {returned.map((rm, i) => (
              <tr key={i}>
                <td style={{ textAlign: 'center' }}>{i + 1}</td>
                <td>{nomCell(rm.nomenclature_id)}</td>
                {retMulti && <td>{whName.get(retWh(rm) ?? '') ?? '—'}</td>}
                <td style={{ textAlign: 'center' }}>{uomOf(rm)}</td>
                <td style={{ textAlign: 'right' }}>{fmtQty(rm.quantity)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </>)}

      <p style={{ margin: '0.5rem 0' }}>
        Продукцію вироблено з давальницької сировини замовника; сировина
        залишалась власністю замовника протягом переробки.
      </p>
      {d.comment && <p style={{ margin: '0.5rem 0', color: '#333' }}>Коментар: {d.comment}</p>}

      <table style={{ width: '100%', marginTop: 28, fontSize: '10.5pt' }}>
        <tbody>
          <tr>
            <td style={{ width: '50%', verticalAlign: 'top' }}>
              <strong>Передав (представник переробника)</strong><br /><br />
              _______________________
              <br /><span style={{ color: '#333', fontSize: '9pt' }}>підпис, П.І.Б.</span>
            </td>
            <td style={{ width: '50%', verticalAlign: 'top' }}>
              <strong>Прийняв (МВО складу)</strong><br /><br />
              _______________________
              <br /><span style={{ color: '#333', fontSize: '9pt' }}>підпис, П.І.Б.</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
